import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtApiError } from "@/lib/api";
import { BRAND } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Mail, Lock, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const emailRef = useRef(null);
  const passRef = useRef(null);

  // Sync browser autofill into React state (autofill often bypasses onChange).
  useEffect(() => {
    const id = setInterval(() => {
      const em = emailRef.current?.value || "";
      const pw = passRef.current?.value || "";
      if (em !== form.email || pw !== form.password) {
        setForm({ email: em, password: pw });
      }
    }, 300);
    return () => clearInterval(id);
  }, [form.email, form.password]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    // Fallback: read latest DOM values in case autofill happened between renders.
    const email = emailRef.current?.value || form.email;
    const password = passRef.current?.value || form.password;
    if (!email || !password) {
      setErr("Please enter email and password.");
      setBusy(false);
      return;
    }
    try {
      const u = await login(email, password);
      const next = sp.get("next") || (u.role === "admin" || u.role === "support" ? "/admin" : u.role === "vendor" ? "/vendor" : "/dashboard");
      nav(next);
    } catch (e) {
      setErr(fmtApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-slate-950 text-white p-10 lg:p-14 relative overflow-hidden">
        <div className="hero-grain absolute inset-0" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAND.logoUrl} alt="EquipmentHub" className="h-12 w-12 object-contain" style={{ mixBlendMode: "screen" }} />
            <span className="font-display font-black text-white">EquipmentHub India</span>
          </Link>
          <div className="mt-16 max-w-md">
            <div className="label-eyebrow text-ehi-accent mb-3">Welcome back</div>
            <h2 className="font-display font-black text-4xl leading-tight">
              India&apos;s largest B2B equipment marketplace,<br />
              <span className="text-ehi-accent">now at your fingertips.</span>
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Log in to manage your fleet, receive matched leads, or track your requirements &amp; quotes.
            </p>
          </div>
        </div>
        <div className="relative text-xs text-slate-400">
          © {new Date().getFullYear()} EquipmentHub India · Connecting Equipment &amp; Projects
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="md:hidden mb-6 flex items-center gap-2">
            <img src={BRAND.logoUrl} alt="EquipmentHub" className="h-12 w-12 object-contain" style={{ mixBlendMode: "darken" }} />
            <span className="font-display font-black text-ehi-primary">EquipmentHub India</span>
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900">Log in to your account</h1>
          <p className="text-slate-600 mt-1">New here? <Link to="/register" className="text-ehi-primary font-semibold hover:underline">Create an account</Link></p>
          <p className="text-xs text-slate-500 mt-2">Approved EquipmentHub partner? <Link to="/partner-login" className="text-emerald-700 font-bold hover:underline" data-testid="login-partner-signin">Sign in to the Partner Portal &rarr;</Link></p>

          {/* Google login temporarily disabled per business rule — kept in code for future re-enable via admin config
          <button type="button" onClick={google} className="mt-6 w-full flex items-center justify-center gap-2 border border-slate-200 hover:border-slate-300 py-2.5 rounded-md font-semibold text-sm text-slate-700 transition-colors" data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.3-1.66 3.8-5.5 3.8-3.31 0-6-2.75-6-6.1s2.69-6.1 6-6.1c1.88 0 3.14.8 3.86 1.5l2.63-2.52C16.9 3.2 14.68 2.2 12 2.2 6.98 2.2 3 6.18 3 11.2s3.98 9 9 9c5.2 0 8.63-3.65 8.63-8.8 0-.6-.06-1.03-.15-1.4H12z" /></svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
          </div>
          */}
          <div className="mt-6"></div>

          <div className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input ref={emailRef} required type="email" autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10" data-testid="login-email" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input ref={passRef} required type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-10" data-testid="login-password" />
              </div>
            </div>
          </div>

          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-xs font-semibold text-slate-600 hover:text-ehi-primary" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          {err && <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="login-error">{err}</div>}

          <button disabled={busy} className="btn-accent w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-submit">
            {busy ? "Logging in…" : (<>Log in <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
